import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X, KeyboardIcon, ScanLine } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const READER_ID = "field-scanner-reader";

/**
 * Camera-based QR scanner for field officers.
 * Accepts either a full customer URL (…/h/<uuid>) or a bare UUID.
 * On successful decode: POST /api/field/scans → navigate to detail view.
 */
export default function FieldScanner() {
  const nav = useNavigate();
  const scannerRef = useRef(null);
  const runningRef = useRef(false);
  const [status, setStatus] = useState("idle"); // idle | starting | scanning | error
  const [error, setError] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [manualId, setManualId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const extractHouseholdId = (raw) => {
    if (!raw) return null;
    const text = String(raw).trim();
    // Match either a plain UUID or …/h/<uuid> in a URL
    const uuidRe = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const mUrl = text.match(/\/h\/([^/?#\s]+)/i);
    if (mUrl && mUrl[1]) {
      const cand = mUrl[1];
      return uuidRe.test(cand) ? cand.toLowerCase() : cand;
    }
    const mBare = text.match(uuidRe);
    if (mBare) return mBare[0].toLowerCase();
    return null;
  };

  const submit = async (hid, source = "qr") => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/field/scans", { household_id: hid, source });
      toast.success(`Opened ${data?.household?.household_name || "household"}`);
      nav(`/field/h/${hid}`);
    } catch (e) {
      const detail = formatDetail(e.response?.data?.detail) || "Scan failed";
      toast.error(detail);
      setError(detail);
      setStatus("error");
    } finally { setSubmitting(false); }
  };

  const onDecode = async (decoded) => {
    const hid = extractHouseholdId(decoded);
    if (!hid) {
      setError(`Unrecognised QR code: ${String(decoded).slice(0, 60)}`);
      setStatus("error");
      return;
    }
    // Stop the camera before navigating away
    await stopScanner();
    await submit(hid, "qr");
  };

  const startScanner = async () => {
    if (runningRef.current) return;
    setStatus("starting"); setError("");
    try {
      if (!scannerRef.current) scannerRef.current = new Html5Qrcode(READER_ID);
      await scannerRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
        onDecode,
        () => { /* per-frame decode failure — noisy, ignore */ },
      );
      runningRef.current = true;
      setStatus("scanning");
    } catch (e) {
      const msg = e?.message || "Camera unavailable. Grant camera permission and try again.";
      setError(msg);
      setStatus("error");
    }
  };

  const stopScanner = async () => {
    if (!runningRef.current || !scannerRef.current) return;
    try { await scannerRef.current.stop(); } catch { /* ignore */ }
    try { await scannerRef.current.clear(); } catch { /* ignore */ }
    runningRef.current = false;
  };

  useEffect(() => {
    startScanner();
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitManual = (e) => {
    e.preventDefault();
    const hid = extractHouseholdId(manualId);
    if (!hid) { setError("Enter a valid household ID or QR link"); return; }
    submit(hid, "manual");
  };

  return (
    <div className="space-y-4" data-testid="field-scanner-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-flame-400 font-semibold">Scan</div>
          <h1 className="font-heading text-xl font-bold text-white mt-0.5">Point at QR code</h1>
        </div>
        <button
          onClick={() => nav("/field")}
          data-testid="field-scanner-close-btn"
          className="text-slate-400 hover:text-white p-2"
          aria-label="Close scanner"
        >
          <X size={20} />
        </button>
      </div>

      {/* Camera viewport */}
      <div className="relative rounded-2xl overflow-hidden bg-black aspect-square border border-slate-800">
        <div id={READER_ID} className="w-full h-full" />
        {/* Overlay guide */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="w-3/5 aspect-square border-2 border-flame-400/80 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
        </div>
        {status === "starting" && (
          <div className="absolute inset-0 grid place-items-center bg-black/40 text-slate-200 text-sm">
            <div className="flex items-center gap-2"><Camera size={16} /> Starting camera…</div>
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 grid place-items-center bg-black/70 text-slate-100 text-sm px-6 text-center">
            <div>
              <div className="text-red-400 font-semibold mb-1">Camera error</div>
              <div className="text-slate-300 text-xs">{error}</div>
              <Button
                onClick={startScanner}
                className="mt-4 bg-brand-600 hover:bg-brand-700 rounded-sm"
                data-testid="field-scanner-retry-btn"
              >Retry</Button>
            </div>
          </div>
        )}
      </div>

      {status === "scanning" && (
        <div className="flex items-center justify-center gap-2 text-xs text-slate-400" data-testid="field-scanner-status">
          <ScanLine size={14} className="text-flame-400" />
          Aim at the QR on the customer&apos;s household card.
        </div>
      )}

      {/* Manual fallback */}
      <div className="pt-2">
        {!manualOpen ? (
          <button
            onClick={() => setManualOpen(true)}
            data-testid="field-scanner-manual-toggle"
            className="w-full flex items-center justify-center gap-2 text-slate-400 hover:text-slate-200 text-xs uppercase tracking-widest font-semibold py-3 border border-dashed border-slate-800 rounded-lg"
          >
            <KeyboardIcon size={14} /> Enter household ID manually
          </button>
        ) : (
          <form onSubmit={submitManual} className="space-y-2" data-testid="field-scanner-manual-form">
            <Input
              autoFocus
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="paste QR link or UUID"
              className="bg-slate-900 border-slate-800 text-white rounded-sm"
              data-testid="field-scanner-manual-input"
            />
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setManualOpen(false)} className="flex-1 rounded-sm" data-testid="field-scanner-manual-cancel">Cancel</Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-brand-600 hover:bg-brand-700 rounded-sm" data-testid="field-scanner-manual-submit">
                {submitting ? "Opening…" : "Open"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
